import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Box, HStack, Spinner, Stack } from "@chakra-ui/react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts"
import { IrrCompanyFund } from "@/types"
import { Text } from "@/components/ui"
import { useColorMode } from "@/contexts"

// ─── Formatters ───────────────────────────────────────────────────────────────

const fmtShort = (v: number) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
  return `$${v.toFixed(0)}`
}

const fmtFull = (v: number) =>
  v < 0
    ? `($${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })})`
    : `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`

const fmtMultiple = (v: number | null) => (v != null ? `${v.toFixed(2)}x` : "—")

// ─── Color by TVPI = (Residual + Realized) / Invested ─────────────────────────

const getBarColor = (residual: number, realized: number, invested: number) => {
  if (invested <= 0) return "#94a3b8"
  const m = (residual + realized) / invested
  if (m >= 2.0) return "#16a34a" // deep green
  if (m >= 1.5) return "#22c55e" // green
  if (m >= 1.0) return "#f59e0b" // amber
  return "#ef4444" // red
}

// ─── Chart modes ──────────────────────────────────────────────────────────────

type ChartMode = "residual" | "invested" | "totalValue" | "realized"

const CHART_MODES: { key: ChartMode; label: string }[] = [
  { key: "residual", label: "Residual" },
  { key: "invested", label: "Invested" },
  { key: "totalValue", label: "Total Value" },
  { key: "realized", label: "Realized" },
]

// Maps each mode to the dataKey used in the chart bar + table sort
const SORT_KEY: Record<ChartMode, "residual" | "invested" | "totalValue" | "realized"> = {
  residual: "residual",
  invested: "invested",
  totalValue: "totalValue",
  realized: "realized",
}

const MODE_LABEL: Record<ChartMode, string> = {
  residual: "Residual Value",
  invested: "Total Invested",
  totalValue: "Total Value (Residual + Realized)",
  realized: "Total Realized",
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChartRow {
  name: string
  residual: number
  realized: number
  totalValue: number
  invested: number
  tvpi: number | null
  fmv: number
  fmvChange: IrrCompanyFund["companies"][0]["fmvChange"]
  sharesLeft: number
  url: string
  esoCompanyId: number
  barColor: string
}

// ─── Custom tooltip ───────────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  const d = payload[0]?.payload as ChartRow
  return (
    <Box
      bg="card.bg"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      p={3}
      shadow="lg"
      minW="230px"
    >
      <Text fontSize="sm" fontWeight="bold" mb={2}>{d.name}</Text>
      <Stack gap={1}>
        <HStack justify="space-between">
          <Text fontSize="xs" color="text.secondary">Residual</Text>
          <Text fontSize="xs" fontWeight="semibold" color="green.500">{fmtFull(d.residual)}</Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="xs" color="text.secondary">Realized</Text>
          <Text fontSize="xs" fontWeight="semibold" color="blue.500">
            {d.realized > 0 ? fmtFull(d.realized) : "—"}
          </Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="xs" color="text.secondary">Total Value</Text>
          <Text fontSize="xs" fontWeight="semibold">{fmtFull(d.totalValue)}</Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="xs" color="text.secondary">Invested</Text>
          <Text fontSize="xs" fontWeight="semibold">{fmtFull(d.invested)}</Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="xs" color="text.secondary">TVPI</Text>
          <Text fontSize="xs" fontWeight="semibold" color={d.barColor}>{fmtMultiple(d.tvpi)}</Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="xs" color="text.secondary">Current FMV</Text>
          <Text fontSize="xs" fontWeight="semibold">{d.fmv > 0 ? `$${d.fmv.toFixed(2)}` : "—"}</Text>
        </HStack>
        {d.fmvChange && (
          <HStack justify="space-between">
            <Text fontSize="xs" color="text.secondary">FMV Change</Text>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color={d.fmvChange.direction === "up" ? "green.500" : d.fmvChange.direction === "down" ? "red.500" : "text.secondary"}
            >
              {d.fmvChange.direction === "up" ? "▲" : d.fmvChange.direction === "down" ? "▼" : ""}
              {" "}{Math.abs(d.fmvChange.pctChange).toFixed(1)}%
            </Text>
          </HStack>
        )}
        {d.sharesLeft > 0 && (
          <HStack justify="space-between">
            <Text fontSize="xs" color="text.secondary">Shares Remaining</Text>
            <Text fontSize="xs" fontWeight="semibold">{d.sharesLeft.toLocaleString()}</Text>
          </HStack>
        )}
      </Stack>
    </Box>
  )
}

// ─── Summary card ─────────────────────────────────────────────────────────────

const SummaryCard = ({
  label,
  value,
  sub,
  color,
}: {
  label: string
  value: string
  sub?: string
  color?: string
}) => (
  <Stack
    gap={0.5}
    px={5}
    py={4}
    borderWidth="1px"
    borderColor="border.default"
    borderRadius="xl"
    bg="card.bg"
    flex="1"
    minW="160px"
  >
    <Text fontSize="xs" color="text.muted" fontWeight="medium">{label}</Text>
    <Text fontSize="2xl" fontWeight="bold" color={color}>{value}</Text>
    {sub && <Text fontSize="xs" color="text.subtle">{sub}</Text>}
  </Stack>
)

// ─── FMV change badge ─────────────────────────────────────────────────────────

const FmvBadge = ({ direction, pct }: { direction: "up" | "down" | "neutral"; pct: number }) => {
  const color = direction === "up" ? "green.500" : direction === "down" ? "red.500" : "text.muted"
  const arrow = direction === "up" ? "▲" : direction === "down" ? "▼" : "—"
  return (
    <Text fontSize="10px" fontWeight="semibold" color={color} whiteSpace="nowrap">
      {arrow} {Math.abs(pct).toFixed(1)}%
    </Text>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  irrCompanyFunds: IrrCompanyFund[]
  isLoading: boolean
}

export const Top20ChartsComponent = ({ irrCompanyFunds, isLoading }: Props) => {
  const navigate = useNavigate()
  const { colorMode } = useColorMode()
  const isDark = colorMode === "dark"
  const [chartMode, setChartMode] = useState<ChartMode>("residual")

  const { allRows, totalResidual, totalInvested, totalRealized, portfolioTvpi, companyCount } =
    useMemo(() => {
      if (!irrCompanyFunds?.length)
        return {
          allRows: [],
          totalResidual: 0,
          totalInvested: 0,
          totalRealized: 0,
          portfolioTvpi: null,
          companyCount: 0,
        }

      // Deduplicate by esoCompanyId (residualValue + totalInvested are cross-fund aggregates).
      // Separately accumulate per-company realized by summing fundRealizeValue across all fund appearances.
      const seen = new Map<number, IrrCompanyFund["companies"][0]>()
      const realizedByCompany = new Map<number, number>()
      for (const fund of irrCompanyFunds) {
        for (const c of fund.companies) {
          if (!seen.has(c.esoCompanyId)) seen.set(c.esoCompanyId, c)
          realizedByCompany.set(
            c.esoCompanyId,
            (realizedByCompany.get(c.esoCompanyId) ?? 0) + (parseFloat(c.fundRealizeValue) || 0)
          )
        }
      }
      const uniqueCompanies = Array.from(seen.values())

      const totalRealized = Array.from(realizedByCompany.values()).reduce((s, v) => s + v, 0)
      const totalResidual = uniqueCompanies.reduce((s, c) => s + (parseFloat(c.residualValue) || 0), 0)
      const totalInvested = uniqueCompanies.reduce((s, c) => s + (parseFloat(c.totalInvested) || 0), 0)
      const portfolioTvpi = totalInvested > 0 ? (totalResidual + totalRealized) / totalInvested : null

      const allRows: ChartRow[] = uniqueCompanies.map((c) => {
        const residual = parseFloat(c.residualValue) || 0
        const invested = parseFloat(c.totalInvested) || 0
        const realized = realizedByCompany.get(c.esoCompanyId) ?? 0
        const totalValue = residual + realized
        const tvpi = invested > 0 ? totalValue / invested : null
        return {
          name: c.companyName,
          residual,
          realized,
          totalValue,
          invested,
          tvpi,
          fmv: parseFloat(c.fmv) || 0,
          fmvChange: c.fmvChange,
          sharesLeft: c.sharesLeft,
          url: c.companyUrl,
          esoCompanyId: c.esoCompanyId,
          barColor: getBarColor(residual, realized, invested),
        }
      })

      return {
        allRows,
        totalResidual,
        totalInvested,
        totalRealized,
        portfolioTvpi,
        companyCount: uniqueCompanies.length,
      }
    }, [irrCompanyFunds])

  // Re-sort top 20 whenever chart mode changes
  const top20 = useMemo(() => {
    if (!allRows.length) return []
    const key = SORT_KEY[chartMode]
    return [...allRows].sort((a, b) => b[key] - a[key]).slice(0, 20)
  }, [allRows, chartMode])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" pt={20}>
        <Spinner size="xl" />
      </Box>
    )
  }

  if (!top20.length) {
    return (
      <Box textAlign="center" pt={20}>
        <Text color="text.muted">No IRR data available yet.</Text>
      </Box>
    )
  }

  const axisColor = isDark ? "#9ca3af" : "#6b7280"
  const gridColor = isDark ? "#374151" : "#e5e7eb"
  const chartHeight = top20.length * 56 + 60
  const sortKey = SORT_KEY[chartMode]

  return (
    <Stack gap={6} pb={8}>
      {/* Summary cards */}
      <HStack flexWrap="wrap" gap={4}>
        <SummaryCard
          label="Portfolio Residual"
          value={fmtShort(totalResidual)}
          sub="Current ESO equity value"
          color="green.500"
        />
        <SummaryCard
          label="Total Invested"
          value={fmtShort(totalInvested)}
          sub="Capital deployed"
        />
        <SummaryCard
          label="Total Realized"
          value={fmtShort(totalRealized)}
          sub="Cash returned to ESO"
          color="blue.500"
        />
        <SummaryCard
          label="Portfolio TVPI"
          value={portfolioTvpi != null ? `${portfolioTvpi.toFixed(2)}x` : "—"}
          sub="(Residual + Realized) / Invested"
          color={
            portfolioTvpi == null
              ? undefined
              : portfolioTvpi >= 1.5
              ? "green.500"
              : portfolioTvpi >= 1.0
              ? "yellow.500"
              : "red.500"
          }
        />
        <SummaryCard
          label="Active Companies"
          value={String(companyCount)}
          sub="Portfolio positions"
        />
      </HStack>

      {/* Chart mode tab bar */}
      <HStack gap={0} borderBottomWidth="1px" borderColor="border.default">
        {CHART_MODES.map((m) => {
          const isActive = chartMode === m.key
          return (
            <Box
              key={m.key}
              px={4}
              py={2}
              cursor="pointer"
              borderBottomWidth="2px"
              borderBottomColor={isActive ? "blue.500" : "transparent"}
              onClick={() => setChartMode(m.key)}
              mr={1}
            >
              <Text
                fontSize="sm"
                fontWeight={isActive ? "semibold" : "medium"}
                color={isActive ? "blue.500" : "text.secondary"}
              >
                Top 20 by {m.label}
              </Text>
            </Box>
          )
        })}
      </HStack>

      {/* Legend */}
      <HStack gap={5} flexWrap="wrap">
        <Text fontSize="xs" color="text.muted">Bar color = TVPI (Residual + Realized) / Invested:</Text>
        {[
          { color: "#16a34a", label: "≥ 2.0x" },
          { color: "#22c55e", label: "1.5–2.0x" },
          { color: "#f59e0b", label: "1.0–1.5x" },
          { color: "#ef4444", label: "< 1.0x" },
          { color: "#94a3b8", label: "No data" },
        ].map(({ color, label }) => (
          <HStack key={label} gap={1.5}>
            <Box w={3} h={3} borderRadius="sm" bg={color} flexShrink={0} />
            <Text fontSize="xs" color="text.secondary">{label}</Text>
          </HStack>
        ))}
      </HStack>

      {/* Bar chart */}
      <Box
        bg="card.bg"
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="xl"
        p={6}
        overflowX="auto"
      >
        <Text fontSize="md" fontWeight="semibold" mb={1}>
          Top {top20.length} Companies by {MODE_LABEL[chartMode]}
        </Text>
        <Text fontSize="xs" color="text.muted" mb={5}>
          Click any bar to open the company page.
          {chartMode !== "invested" && " Outer bar = primary metric, inner bar = Total Invested."}
        </Text>

        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            layout="vertical"
            data={top20}
            margin={{ left: 8, right: 90, top: 4, bottom: 4 }}
            barCategoryGap="28%"
            barGap={3}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridColor} />
            <XAxis
              type="number"
              tickFormatter={fmtShort}
              tick={{ fontSize: 11, fill: axisColor }}
              axisLine={{ stroke: gridColor }}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={172}
              tick={{ fontSize: 12, fill: axisColor }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: string) => (v.length > 22 ? v.slice(0, 21) + "…" : v)}
              style={{ cursor: "pointer" }}
              onClick={(data: any) => {
                const row = top20.find((r) => r.name === data.value)
                if (row) navigate(row.url)
              }}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}
            />

            {/* Background reference bar: Total Invested (shown for all modes except invested) */}
            {chartMode !== "invested" && (
              <Bar dataKey="invested" name="Total Invested" barSize={10} radius={[0, 3, 3, 0]}>
                {top20.map((entry) => (
                  <Cell
                    key={entry.esoCompanyId}
                    fill={isDark ? "#334155" : "#cbd5e1"}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(entry.url)}
                  />
                ))}
              </Bar>
            )}

            {/* Primary metric bar */}
            <Bar dataKey={sortKey} name={MODE_LABEL[chartMode]} barSize={20} radius={[0, 3, 3, 0]}>
              {top20.map((entry) => (
                <Cell
                  key={entry.esoCompanyId}
                  fill={entry.barColor}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(entry.url)}
                />
              ))}
              <LabelList
                dataKey={sortKey}
                position="right"
                formatter={(v: any) => fmtShort(v as number)}
                style={{ fontSize: 11, fill: axisColor, fontWeight: 600 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Ranked table */}
      <Box
        bg="card.bg"
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="xl"
        overflow="hidden"
      >
        <Box px={6} py={4} borderBottomWidth="1px" borderColor="border.default">
          <Text fontSize="md" fontWeight="semibold">Ranked Positions</Text>
        </Box>
        <Box overflowX="auto">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: isDark ? "#1e2a4a" : "#f8fafc" }}>
                {["#", "Company", "Residual", "Realized", "Total Value", "Invested", "TVPI", "FMV", "FMV Δ"].map(
                  (h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 14px",
                        textAlign: h === "#" || h === "Company" ? "left" : "right",
                        fontWeight: 600,
                        color: axisColor,
                        whiteSpace: "nowrap",
                        borderBottom: `1px solid ${gridColor}`,
                        fontSize: 11,
                        letterSpacing: "0.03em",
                        textTransform: "uppercase",
                      }}
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {top20.map((row, i) => (
                <tr
                  key={row.esoCompanyId}
                  style={{
                    background: isDark
                      ? i % 2 === 0
                        ? "#16213e"
                        : "#1a2744"
                      : i % 2 === 0
                      ? "#ffffff"
                      : "#f8fafc",
                    cursor: "pointer",
                    transition: "background 0.1s",
                  }}
                  onClick={() => navigate(row.url)}
                  onMouseEnter={(e) => {
                    ;(e.currentTarget as HTMLTableRowElement).style.background = isDark
                      ? "#1e2e5a"
                      : "#eff6ff"
                  }}
                  onMouseLeave={(e) => {
                    ;(e.currentTarget as HTMLTableRowElement).style.background = isDark
                      ? i % 2 === 0
                        ? "#16213e"
                        : "#1a2744"
                      : i % 2 === 0
                      ? "#ffffff"
                      : "#f8fafc"
                  }}
                >
                  <td style={{ padding: "10px 14px", color: axisColor, fontWeight: 500, fontSize: 12 }}>
                    {i + 1}
                  </td>
                  <td style={{ padding: "10px 14px", fontWeight: 600 }}>
                    <HStack gap={2}>
                      <Box
                        display="inline-block"
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        style={{ background: row.barColor, flexShrink: 0 }}
                      />
                      {row.name}
                    </HStack>
                  </td>
                  <td
                    style={{
                      padding: "10px 14px",
                      textAlign: "right",
                      fontWeight: chartMode === "residual" ? 700 : 400,
                      color: chartMode === "residual" ? "#22c55e" : undefined,
                    }}
                  >
                    {fmtFull(row.residual)}
                  </td>
                  <td
                    style={{
                      padding: "10px 14px",
                      textAlign: "right",
                      fontWeight: chartMode === "realized" ? 700 : 400,
                      color: chartMode === "realized" ? "#60a5fa" : undefined,
                    }}
                  >
                    {row.realized > 0 ? fmtFull(row.realized) : "—"}
                  </td>
                  <td
                    style={{
                      padding: "10px 14px",
                      textAlign: "right",
                      fontWeight: chartMode === "totalValue" ? 700 : 400,
                    }}
                  >
                    {fmtFull(row.totalValue)}
                  </td>
                  <td
                    style={{
                      padding: "10px 14px",
                      textAlign: "right",
                      fontWeight: chartMode === "invested" ? 700 : 400,
                    }}
                  >
                    {fmtFull(row.invested)}
                  </td>
                  <td
                    style={{
                      padding: "10px 14px",
                      textAlign: "right",
                      fontWeight: 600,
                      color: row.barColor,
                    }}
                  >
                    {fmtMultiple(row.tvpi)}
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "right" }}>
                    {row.fmv > 0 ? `$${row.fmv.toFixed(2)}` : "—"}
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "right" }}>
                    {row.fmvChange ? (
                      <FmvBadge direction={row.fmvChange.direction} pct={row.fmvChange.pctChange} />
                    ) : (
                      <Text fontSize="xs" color="text.subtle">—</Text>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
    </Stack>
  )
}
